// Access matrix context — fetches /api/AccessCenter/get-access once on
// mount, joins with the logged-in employee's role, and exposes a
// hasAccess(category, pageName) helper that every navbar item + page
// route consults. Single source of truth for "who can see what".
//
// Wrap the app once in <AccessProvider> (see src/App.js). Then:
//   const { hasAccess, hasAnyAccess, currentUser, ready } = useAccess();
//
// And in any page that needs to be gated:
//   const { ready } = useAccessGuard("dSpr", "Reports");
//   if (!ready) return <LoadingShell />;
//
// If the user's role doesn't have access per the matrix, useAccessGuard
// redirects to /login (or to a fallback you pass in).

import React, {
    createContext, useContext, useEffect, useState, useCallback, useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./api";

const AccessContext = createContext({
    matrix: [],
    currentUser: null,
    ready: false,
    hasAccess: () => false,
    hasAnyAccess: () => false,
    refetch: async () => {},
});

// Maps emp_access_level → access_levels DB column
const ROLE_COL = {
    "Admin":     "admin_access",
    "Sub Admin": "subadmin_access",
    "Manager":   "manager_access",
    "User":      "user_access",
};

// App-level kill switch. If admin sets `app_dSpr = 0` on the Emp_App_Access
// page, every dSpr surface (navbar items + dadmin routes) goes dark for
// that employee. The /api/dspr/review/check endpoint enforces the same on
// the public review form, so the kill switch is total.
//
// app_dSpr defaults to 1 in the DB now (everyone can fill self-review),
// so this only fires when admin explicitly revokes for a specific person.
//
// Maps category in access_levels → app_* column on the employee row.
const CATEGORY_TO_APP_FLAG = {
    "dSpr":  "app_dSpr",
    "dNews": "app_dNews",
};
function appRevoked(category, user) {
    if (!user) return false;
    const col = CATEGORY_TO_APP_FLAG[category];
    if (!col) return false;
    return user[col] === 0;
}

export function AccessProvider({ children }) {
    const [matrix, setMatrix]           = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [matrixReady, setMatrixReady] = useState(false);
    const [userReady, setUserReady]     = useState(false);

    // Extracted so Login can call this after a successful POST /login -
    // the AccessProvider was mounted while the user was unauthenticated
    // on /Login, so currentUser was null. Without a re-fetch on login,
    // every ProtectedRoute would still see null and bounce back to login.
    const refetch = useCallback(async () => {
        setMatrixReady(false);
        setUserReady(false);

        await Promise.all([
            (async () => {
                try {
                    const res = await apiFetch("/api/AccessCenter/get-access", { credentials: "include" });
                    const data = await res.json();
                    if (data?.success) setMatrix(data.data || []);
                } catch (e) {
                    console.warn("[AccessContext] matrix fetch failed:", e.message);
                } finally {
                    setMatrixReady(true);
                }
            })(),
            (async () => {
                try {
                    const res = await apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" });
                    const data = await res.json();
                    if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                        setCurrentUser(null);
                    } else {
                        setCurrentUser(Array.isArray(data) ? data[0] : data);
                    }
                } catch {
                    setCurrentUser(null);
                } finally {
                    setUserReady(true);
                }
            })(),
        ]);
    }, []);

    // First load on app mount.
    useEffect(() => { refetch(); }, [refetch]);

    const ready = matrixReady && userReady;

    // Build a O(1) lookup so hasAccess doesn't scan the full matrix on
    // every navbar item check. Keyed by "Category::Page Name".
    const matrixMap = useMemo(() => {
        const m = new Map();
        for (const row of matrix) m.set(`${row.category}::${row.page_name}`, row);
        return m;
    }, [matrix]);

    const hasAccess = useCallback((category, pageName) => {
        if (!currentUser?.emp_access_level) return false;
        // App-level revoke wins over the matrix - mirrors the dSlip login
        // check (employee.app_dSlip === 0 blocks all dSlip access).
        if (appRevoked(category, currentUser)) return false;
        const col = ROLE_COL[currentUser.emp_access_level];
        if (!col) return false;
        const row = matrixMap.get(`${category}::${pageName}`);
        if (!row) return false;
        return row[col] === 1;
    }, [currentUser, matrixMap]);

    // True if the role can access AT LEAST ONE page in the category.
    // Use this to decide whether to render a whole nav section.
    const hasAnyAccess = useCallback((category) => {
        if (!currentUser?.emp_access_level) return false;
        if (appRevoked(category, currentUser)) return false;
        const col = ROLE_COL[currentUser.emp_access_level];
        if (!col) return false;
        for (const row of matrix) {
            if (row.category === category && row[col] === 1) return true;
        }
        return false;
    }, [currentUser, matrix]);

    const value = useMemo(() => ({
        matrix, currentUser, ready, hasAccess, hasAnyAccess, refetch,
    }), [matrix, currentUser, ready, hasAccess, hasAnyAccess, refetch]);

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export const useAccess = () => useContext(AccessContext);

// Page-level guard. Place at the top of every protected page:
//   const { ready } = useAccessGuard("dSpr", "Reports");
//   if (!ready) return null;   // or a loader
//
// Behavior:
//   • Waits for the matrix + user fetch to complete
//   • If user has no access, redirects to `fallback` (default /login)
//   • Returns { ready: true } once access is confirmed
export function useAccessGuard(category, pageName, fallback = "/login") {
    const { ready, hasAccess, currentUser } = useAccess();
    const navigate = useNavigate();
    const [allowed, setAllowed] = useState(false);

    useEffect(() => {
        if (!ready) return;
        if (!currentUser?.emp_id) {
            navigate("/login");
            return;
        }
        if (!hasAccess(category, pageName)) {
            // No access → bounce. /login keeps it simple; pages that want
            // a softer landing can pass their own fallback.
            navigate(fallback);
            return;
        }
        setAllowed(true);
    }, [ready, currentUser, hasAccess, category, pageName, fallback, navigate]);

    return { ready: allowed };
}

// Route-level guard. Wrap any <Route element={...}> page that needs the
// matrix to gate access. Replaces 29 per-page useAccessGuard calls with
// a single decoration in App.js:
//
//   <Route path="/dSpr_Reports" element={
//     <ProtectedRoute category="dSpr" page="Reports">
//       <DSprReports navSize={navSize} />
//     </ProtectedRoute>
//   } />
//
// While the matrix + user are loading, renders nothing (instant blank,
// then the page once allowed). If the user lacks access, redirects.
export function ProtectedRoute({ category, page, fallback = "/login", children }) {
    const { ready, hasAccess, currentUser } = useAccess();
    const navigate = useNavigate();

    useEffect(() => {
        if (!ready) return;
        if (!currentUser?.emp_id) {
            navigate("/login");
            return;
        }
        if (!hasAccess(category, page)) {
            navigate(fallback);
        }
    }, [ready, currentUser, hasAccess, category, page, fallback, navigate]);

    if (!ready) return null;
    if (!currentUser?.emp_id) return null;
    if (!hasAccess(category, page)) return null;
    return children;
}
